<template>
  <div class="cv3-page">
    <notifications group="auth" position="bottom right" />

    <!-- ── Barra superior sticky ── -->
    <div class="cv3-topbar">
      <v-btn small tile outlined color="info" @click="$router.push('/promociones-v3')">
        <v-icon left>mdi-arrow-left-bold</v-icon> Volver
      </v-btn>

      <v-icon color="blue darken-4" class="ml-3">{{ editMode ? 'mdi-tag-edit-outline' : 'mdi-tag-plus-outline' }}</v-icon>
      <span class="subtitle-1 font-weight-bold ml-1">{{ editMode ? 'Editar Promoción' : 'Nueva Promoción' }}</span>

      <v-spacer />

      <!-- Indicadores de sección -->
      <div class="cv3-steps">
        <div class="cv3-step" :class="sec1Ok ? 'cv3-step--ok' : 'cv3-step--pending'">
          <v-icon x-small>{{ sec1Ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
          <span>General</span>
        </div>
        <v-icon x-small color="grey lighten-1">mdi-chevron-right</v-icon>
        <div class="cv3-step" :class="sec2Ok ? 'cv3-step--ok' : 'cv3-step--pending'">
          <v-icon x-small>{{ sec2Ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
          <span>Fechas</span>
        </div>
        <v-icon x-small color="grey lighten-1">mdi-chevron-right</v-icon>
        <div class="cv3-step" :class="sec3Ok ? 'cv3-step--ok' : 'cv3-step--pending'">
          <v-icon x-small>{{ sec3Ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
          <span>Reglas</span>
        </div>
      </div>

      <v-btn
        small outlined color="blue darken-4" class="ml-2"
        @click="dialogPlantilla = true"
        :disabled="reglas.length === 0"
      >
        <v-icon left small>mdi-bookmark-plus-outline</v-icon>
        Guardar plantilla
      </v-btn>

      <v-btn
        color="blue darken-4" dark depressed :loading="saving"
        class="ml-2" @click="guardar"
      >
        <v-icon left small>mdi-content-save-outline</v-icon>
        {{ editMode ? 'Guardar Cambios' : 'Guardar Promoción' }}
      </v-btn>
    </div>

    <!-- Dialog guardar plantilla -->
    <v-dialog v-model="dialogPlantilla" width="400" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold py-4 px-5">
          <v-icon color="primary" class="mr-2">mdi-bookmark-plus-outline</v-icon>
          Guardar como Plantilla
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <p class="caption grey--text mb-3">
            Guarda las reglas actuales como plantilla para reutilizarlas en futuras promocións.
          </p>
          <p class="field-label mb-1">NOMBRE DE LA PLANTILLA <span class="red--text">*</span></p>
          <v-text-field
            v-model="plantillaNombre"
            outlined dense hide-details
            placeholder="Ej. Diplomados Estándar"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogPlantilla = false; plantillaNombre = ''">Cancelar</v-btn>
          <v-btn color="primary" depressed small :disabled="!plantillaNombre.trim()" @click="saveAsTemplate">
            <v-icon left x-small>mdi-bookmark-check-outline</v-icon> Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-progress-linear v-if="saving" indeterminate color="blue darken-4" height="3" />

    <!-- ── Cuerpo ── -->
    <div class="cv3-body">

      <v-alert v-if="errorMsg" type="error" dense outlined dismissible class="mb-4" @input="errorMsg = ''">
        <strong>Atención:</strong> {{ errorMsg }}
      </v-alert>

      <!-- ─────────────────────────────── 1. Información General -->
      <v-card outlined class="mb-5 section-card" :class="sec1Ok ? 'section-card--ok' : ''">
        <div class="section-card-title">
          <div class="d-flex align-center">
            <div class="section-num">1</div>
            <v-icon color="blue darken-4" class="ml-3 mr-2">mdi-file-document-outline</v-icon>
            <span class="subtitle-1 font-weight-bold">Información General</span>
          </div>
          <v-icon v-if="sec1Ok" color="green darken-1">mdi-check-circle</v-icon>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row dense>
            <v-col cols="12" md="8">
              <p class="field-label mb-1">NOMBRE DE LA PROMOCIÓN <span class="red--text">*</span></p>
              <v-text-field
                v-model="nombre"
                outlined dense hide-details
                placeholder="Ej. DESCUENTO FEBRERO"
                :success="!!nombre.trim()"
              />
            </v-col>
            <v-col cols="12" md="4">
              <p class="field-label mb-1">ESTADO GENERAL <span class="red--text">*</span></p>
              <v-select
                v-model="estado"
                :items="estadoOptions"
                outlined dense hide-details
                placeholder="Seleccionar..."
                :success="estado !== null && estado !== ''"
              >
                <template v-slot:selection="{ item }">
                  <v-chip x-small :color="estadoColor(item.value)" dark class="font-weight-bold">{{ item.text }}</v-chip>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <p class="field-label mb-1 mt-3">UNIDAD DE NEGOCIO <span class="red--text">*</span></p>
              <v-select
                v-model="unidadSeleccionada"
                :items="unidadesItems"
                outlined dense hide-details
                placeholder="Seleccionar unidad..."
                :loading="loadingUnidades"
                clearable
                :success="!!unidadSeleccionada"
                @change="onUnidadChange"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="grey" class="mr-1">mdi-domain</v-icon>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12">
              <p class="field-label mb-1 mt-3">DESCRIPCIÓN</p>
              <v-textarea
                v-model="descripcion"
                outlined dense hide-details auto-grow
                placeholder="Detalles sobre la promoción..."
                rows="2"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ─────────────────────────────── 2. Reglas de Descuento -->
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <div class="section-num">2</div>
          <v-icon color="blue darken-4" class="ml-3 mr-2">mdi-percent-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Reglas de Descuento</span>
          <v-chip v-if="reglas.length > 0" x-small color="blue darken-4" dark class="ml-2">
            {{ reglas.length }}
          </v-chip>
          <!-- Toggle de vista -->
          <v-btn-toggle v-if="reglas.length > 0" v-model="vistaTabla" mandatory dense class="ml-3">
            <v-btn :value="false" x-small outlined>
              <v-icon x-small left>mdi-card-text-outline</v-icon>Tarjetas
            </v-btn>
            <v-btn :value="true" x-small outlined>
              <v-icon x-small left>mdi-table</v-icon>Tabla
            </v-btn>
          </v-btn-toggle>
        </div>
        <v-btn small color="blue darken-4" dark depressed @click="agregarRegla">
          <v-icon left small>mdi-plus</v-icon> Agregar Regla
        </v-btn>
      </div>

      <!-- Empty state -->
      <v-card v-if="reglas.length === 0" outlined class="mb-5" style="border-style:dashed;">
        <v-card-text class="text-center py-10">
          <v-icon size="48" color="grey lighten-2">mdi-percent-outline</v-icon>
          <div class="subtitle-2 grey--text text--darken-1 mt-3">No hay reglas de descuento</div>
          <div class="caption grey--text mt-1 mb-5">
            Agrega reglas para configurar precios, modalidad y conceptos de descuento.
          </div>
          <v-btn outlined color="blue darken-4" small @click="agregarRegla">
            <v-icon left small>mdi-plus</v-icon> Agregar Primera Regla
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- ── Vista Tabla ── -->
      <div v-if="vistaTabla && reglas.length > 0 && tablaData" class="xtabla-wrap mb-4">
        <div
          v-for="grupo in tablaData.grupos"
          :key="grupo.nombre"
          class="xtabla-grupo mb-5"
        >
          <div class="xtabla-cat-hdr">
            <v-icon small color="white" class="mr-2">mdi-shape-outline</v-icon>
            {{ grupo.nombre || 'Sin categoría' }}
            <v-chip x-small dark color="white" class="ml-2" style="color:#1565c0;">
              {{ grupo.filas.length }} regla{{ grupo.filas.length !== 1 ? 's' : '' }}
            </v-chip>
          </div>
          <div class="xtabla-scroll">
            <table class="xtabla">
              <thead>
                <tr>
                  <th class="xt-th xt-th-area" rowspan="2">ÁREA</th>
                  <th class="xt-th xt-th-meta" rowspan="2">MODALIDAD<br>/ PLAN</th>
                  <th
                    v-for="c in tablaData.conceptos" :key="'gh-'+c"
                    colspan="5" class="xt-th xt-th-group"
                  >{{ c }}</th>
                  <th colspan="4" class="xt-th xt-th-group xt-th-group--summary">RESUMEN</th>
                </tr>
                <tr>
                  <th
                    v-for="col in tablaColumnasSub" :key="col.key"
                    :class="['xt-th', 'xt-th-sub', col.cls]"
                  >{{ col.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(fila, fi) in grupo.filas"
                  :key="'r'+fi"
                  :class="fi % 2 === 0 ? 'xt-tr-even' : 'xt-tr-odd'"
                >
                  <td class="xt-td xt-td-area">{{ labelArea(fila.regla.area) || '—' }}</td>
                  <td class="xt-td xt-td-meta">
                    <div v-if="fila.regla.modalidad" class="xt-badge xt-badge--purple">{{ labelModalidad(fila.regla.modalidad) }}</div>
                    <div v-if="fila.regla.planPago" class="xt-badge xt-badge--orange">{{ fila.regla.planPago === 'credito' ? 'Crédito' : 'Contado' }}</div>
                    <div v-if="fila.regla.tipoPrecio" class="xt-badge xt-badge--brown">{{ labelTipoPrecio(fila.regla.tipoPrecio) }}</div>
                  </td>
                  <td
                    v-for="celda in getCeldasFila(fila)" :key="celda.key"
                    :class="celda.cls"
                    :colspan="celda.colspan || undefined"
                  >
                    <span v-if="celda.zero" class="xt-zero">0%</span>
                    <span v-else>{{ celda.val }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Reglas (tarjetas) -->
      <template v-if="!vistaTabla">
      <v-card
        v-for="(regla, ri) in reglas"
        :key="ri"
        outlined
        class="mb-4 regla-card"
      >
        <!-- Cabecera -->
        <div class="regla-hdr">
          <v-chip small color="blue darken-4" dark class="mr-3 flex-shrink-0">Regla #{{ ri + 1 }}</v-chip>

          <!-- Resumen dinámico cuando hay datos -->
          <div class="d-flex align-center flex-wrap" style="gap:4px; flex:1; min-width:0;">
            <template v-if="regla.categoria || regla.area || regla.modalidad">
              <v-chip v-if="labelCategoria(regla.categoria)" x-small outlined color="indigo">
                {{ labelCategoria(regla.categoria) }}
              </v-chip>
              <v-chip v-if="labelArea(regla.area)" x-small outlined color="teal">
                {{ labelArea(regla.area) }}
              </v-chip>
              <v-chip v-if="regla.modalidad" x-small outlined color="purple">
                {{ labelModalidad(regla.modalidad) }}
              </v-chip>
              <v-chip v-if="regla.planPago" x-small outlined color="deep-orange">
                {{ regla.planPago === 'credito' ? 'Crédito' : 'Contado' }}
              </v-chip>
            </template>
            <span v-else class="caption grey--text text--darken-1">Nueva Regla de Descuento</span>
          </div>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="duplicarRegla(ri)">
                <v-icon color="grey darken-1">mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>Duplicar regla</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="eliminarRegla(ri)">
                <v-icon color="red darken-1">mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>Eliminar regla</span>
          </v-tooltip>
        </div>

        <!-- Campos -->
        <v-card-text class="pa-4 pb-2">
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <p class="field-label mb-1">CATEGORÍA</p>
              <v-select
                v-model="regla.categoria"
                :items="categoriasItems"
                outlined dense hide-details
                placeholder="Seleccionar..."
                :loading="loadingCategorias"
                :disabled="!unidadSeleccionada"
                clearable
              >
                <template v-slot:no-data>
                  <div class="pa-2 caption grey--text text-center">
                    {{ unidadSeleccionada ? 'Sin categorías' : 'Selecciona una unidad primero' }}
                  </div>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="d-flex align-center mb-1" style="gap:6px;">
                <p class="field-label mb-0">ÁREA (GraphQL)</p>
                <v-btn
                  v-if="areaOptionsRest.length > 0"
                  x-small icon color="blue darken-2"
                  title="Ver áreas REST y análisis de similitud"
                  @click="reglaAreasAnalisis = regla; dialogAreasAnalisis = true"
                >
                  <v-icon x-small>mdi-compare-horizontal</v-icon>
                </v-btn>
              </div>
              <v-select
                v-model="regla.area"
                :items="areasItems"
                outlined dense hide-details
                placeholder="Seleccionar..."
                :loading="loadingAreas"
                :disabled="!unidadSeleccionada"
                clearable
                @change="onAreaChange(regla)"
              >
                <template v-slot:no-data>
                  <div class="pa-2 caption grey--text text-center">
                    {{ unidadSeleccionada ? 'Sin áreas' : 'Selecciona una unidad primero' }}
                  </div>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <p class="field-label mb-1">MODALIDAD</p>
              <v-select
                v-model="regla.modalidad"
                :items="modalidadItemsParaRegla(regla)"
                outlined dense hide-details
                placeholder="Seleccionar..."
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <p class="field-label mb-1">PLAN PAGO</p>
              <v-select
                v-model="regla.planPago"
                :items="planPagoOptions"
                outlined dense hide-details
                placeholder="Seleccionar..."
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <p class="field-label mb-1">TIPO PRECIO</p>
              <v-select
                v-model="regla.tipoPrecio"
                :items="tipoPrecioOptions"
                outlined dense hide-details
                placeholder="Seleccionar..."
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <p class="field-label mb-1">ESTADO</p>
              <v-select
                v-model="regla.estadoPrograma"
                :items="estadoReglaOptions"
                item-text="text"
                item-value="value"
                outlined dense hide-details
                placeholder="Seleccionar..."
                clearable
                multiple
              >
                <template v-slot:selection="{ item }">
                  <v-chip x-small :color="estadoColor(item.value)" dark class="mr-1 mb-1">{{ item.text }}</v-chip>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-if="!item.header" v-bind="attrs" v-on="on" dense>
                    <v-list-item-action class="mr-2">
                      <v-checkbox :input-value="attrs.inputValue" dense hide-details class="ma-0 pa-0" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip x-small :color="estadoColor(item.value)" dark class="mr-2">{{ item.text }}</v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">{{ item.sub }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-subheader v-else class="caption font-weight-bold grey--text text--darken-2 px-4">
                    <v-icon x-small class="mr-1" color="grey darken-2">mdi-store-outline</v-icon>
                    {{ item.header }}
                  </v-subheader>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Tabla de conceptos -->
        <div class="px-4 pb-3">
          <v-divider class="mb-3" />
          <div class="d-flex align-center mb-2">
            <span class="field-label">CONCEPTOS DE DESCUENTO</span>
            <v-spacer />
            <v-btn
              small depressed color="teal darken-1" dark class="mr-2"
              :disabled="!regla.tipoPrecio || !regla.categoria || !regla.area || !regla.modalidad || !regla.planPago"
              @click="consultarPrecios(ri)"
            >
              <v-icon left small>mdi-magnify</v-icon> Consultar Precios
            </v-btn>
            <v-btn small depressed color="blue darken-4" dark @click="abrirConceptos(ri)">
              <v-icon left small>mdi-plus</v-icon> Agregar Concepto
            </v-btn>
          </div>

          <v-data-table
            :headers="conceptosHeaders"
            :items="regla.conceptos"
            hide-default-footer
            disable-pagination
            :items-per-page="-1"
            class="elevation-0 conceptos-tbl"
            dense
          >
            <template v-slot:no-data>
              <div class="text-center py-4 grey--text">
                <v-icon color="grey lighten-2" class="mb-1">mdi-inbox-outline</v-icon>
                <div class="caption">Sin conceptos — usa "+ Agregar Concepto"</div>
              </div>
            </template>

            <template v-slot:[`item.precioBase`]="{ item }">
              <v-text-field
                v-model.number="item.precioBase"
                type="number" min="0" outlined dense hide-details
                style="max-width:90px;" @input="$forceUpdate()"
              />
            </template>
            <template v-slot:[`item.cuotas`]="{ item }">
              <v-text-field
                v-model.number="item.cuotas"
                type="number" min="1" outlined dense hide-details
                style="max-width:60px;" @input="$forceUpdate()"
              />
            </template>
            <template v-slot:[`item.descuento`]="{ item }">
              <div class="d-flex flex-column" style="gap:2px;">
                <div class="d-flex align-center" style="gap:6px;">
                  <v-text-field
                    :value="item.descuento"
                    type="number" min="0"
                    outlined dense hide-details
                    :class="{
                      'desc-at-limit':   item.precioMinimo > 0 && !descuentoSobreLimite(item) && pFinal(item) <= item.precioMinimo && item.descuento > 0,
                      'desc-over-limit': descuentoSobreLimite(item)
                    }"
                    style="width:90px;"
                    @input="onDescuentoInput(item, $event)"
                  />
                  <div class="desc-toggle">
                    <button
                      class="desc-toggle-btn"
                      :class="{ 'desc-toggle-btn--active': item.isPorcentaje }"
                      @click.prevent="item.isPorcentaje = true; item.descuento = Math.min(item.descuento, maxDescuentoPct(item)); $forceUpdate()"
                    >%</button>
                    <button
                      class="desc-toggle-btn"
                      :class="{ 'desc-toggle-btn--active': !item.isPorcentaje }"
                      @click.prevent="item.isPorcentaje = false; $forceUpdate()"
                    >Bs</button>
                  </div>
                </div>
                <span v-if="descuentoSobreLimite(item)" class="desc-over-msg">
                  <v-icon x-small color="red darken-2">mdi-alert-circle</v-icon>
                  Supera el límite máximo
                </span>
              </div>
            </template>
            <template v-slot:[`item.precioMinimo`]="{ item }">
              <span v-if="item.precioMinimo !== null && item.precioMinimo !== undefined && item.precioMinimo !== ''" class="orange--text text--darken-2 caption font-weight-bold">
                Bs {{ parseFloat(item.precioMinimo).toFixed(2) }}
              </span>
              <span v-else class="grey--text caption">—</span>
            </template>
            <template v-slot:[`item.precioFinal`]="{ item }">
              <div class="d-flex flex-column align-end">
                <span class="green--text text--darken-2 font-weight-bold">
                  Bs {{ pFinal(item).toFixed(2) }}
                </span>
                <v-tooltip v-if="item.precioMinimo && pFinal(item) < item.precioMinimo" bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="orange--text text--darken-2 caption d-flex align-center" style="font-size:10px; cursor:help;">
                      <v-icon x-small color="orange darken-2" class="mr-1">mdi-alert</v-icon>
                      bajo mínimo (Bs {{ parseFloat(item.precioMinimo).toFixed(2) }})
                    </span>
                  </template>
                  <span>El precio final (Bs {{ pFinal(item).toFixed(2) }}) es menor al precio mínimo permitido (Bs {{ parseFloat(item.precioMinimo).toFixed(2) }}). Puedes guardar igualmente.</span>
                </v-tooltip>
              </div>
            </template>
            <template v-slot:[`item.valorCuota`]="{ item }">
              <span class="blue--text text--darken-2 font-weight-bold">
                Bs {{ vCuota(item).toFixed(2) }}
              </span>
            </template>
            <template v-slot:[`item.acciones`]="{ index }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="quitarConcepto(ri, index)">
                    <v-icon x-small color="red darken-1">mdi-close</v-icon>
                  </v-btn>
                </template>
                <span>Quitar</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </div>

        <!-- Advertencia bajo mínimo -->
        <v-alert
          v-if="conceptosBajoMinimo(regla).length > 0"
          type="warning" dense text class="ma-3 mb-0 caption"
          icon="mdi-alert-outline"
        >
          {{ conceptosBajoMinimo(regla).length }} concepto(s) con precio final por debajo del mínimo. Puedes guardar igualmente, pero verifica que sea intencional.
        </v-alert>

        <!-- Totales -->
        <v-divider />
        <div class="regla-totals">
          <div class="rtot-cell">
            <div class="rtot-label">
              <v-icon x-small class="mr-1">mdi-cash-register</v-icon>TOTAL BASE
            </div>
            <div class="rtot-value">Bs {{ tBase(regla).toFixed(2) }}</div>
          </div>
          <v-divider vertical />
          <div class="rtot-cell rtot-cell--green">
            <div class="rtot-label rtot-label--green">
              <v-icon x-small class="mr-1">mdi-piggy-bank-outline</v-icon>AHORRO TOTAL
            </div>
            <div class="rtot-value rtot-value--green">Bs {{ tAhorro(regla).toFixed(2) }}</div>
          </div>
          <v-divider vertical />
          <div class="rtot-cell rtot-cell--blue">
            <div class="rtot-label rtot-label--blue">
              <v-icon x-small class="mr-1">mdi-numeric-1-circle-outline</v-icon>PAGO INICIAL (1ERA CUOTA)
            </div>
            <div class="rtot-value rtot-value--blue rtot-value--lg">Bs {{ tPrimeraCuota(regla).toFixed(2) }}</div>
          </div>
          <v-divider vertical />
          <div class="rtot-cell rtot-cell--dark">
            <div class="rtot-label rtot-label--light">
              <v-icon x-small class="mr-1" color="white" style="opacity:.7;">mdi-trophy-outline</v-icon>
              <span style="opacity:.7;">PRECIO FINAL TOTAL</span>
            </div>
            <div class="rtot-value rtot-value--white rtot-value--lg">Bs {{ tFinal(regla).toFixed(2) }}</div>
          </div>
        </div>
      </v-card>
      </template><!-- /vistaTabla -->

      <!-- Agregar otra regla -->
      <div v-if="reglas.length > 0" class="add-otra-regla" @click="agregarRegla">
        <v-icon color="grey darken-1" small class="mr-2">mdi-plus-circle-outline</v-icon>
        <span class="body-2 grey--text text--darken-1">Agregar Otra Regla</span>
      </div>

      <!-- ─────────────────────────────── 3. Fechas de Disponibilidad -->
      <v-card outlined class="mb-5 mt-5 section-card" :class="sec2Ok ? 'section-card--ok' : ''">
        <div class="section-card-title">
          <div class="d-flex align-center">
            <div class="section-num">3</div>
            <v-icon color="blue darken-4" class="ml-3 mr-2">mdi-calendar-range</v-icon>
            <span class="subtitle-1 font-weight-bold">Fechas de Disponibilidad</span>
            <v-chip v-if="seleccionesConteo > 0" x-small color="green darken-1" dark class="ml-3">
              {{ seleccionesConteo }} día{{ seleccionesConteo !== 1 ? 's' : '' }}
            </v-chip>
          </div>
          <div class="d-flex align-center" style="gap:8px;">
            <v-btn v-if="selecciones.length > 0" x-small text color="error" @click="selecciones = []; clickState = null">
              <v-icon x-small left>mdi-close</v-icon> Limpiar
            </v-btn>
            <v-icon v-if="sec2Ok" color="green darken-1">mdi-check-circle</v-icon>
          </div>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row>
            <!-- Calendario -->
            <v-col cols="12" md="7">
              <div class="d-flex align-center justify-space-between mb-4">
                <v-btn icon small @click="prevMonthCal"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <span class="subtitle-2 font-weight-bold">{{ calMesLabel }}</span>
                <v-btn icon small @click="nextMonthCal"><v-icon>mdi-chevron-right</v-icon></v-btn>
              </div>

              <div class="cal-sel-grid">
                <div v-for="d in ['Lu','Ma','Mi','Ju','Vi','Sá','Do']" :key="d" class="cal-sel-head">{{ d }}</div>
                <div v-for="n in calPrimerDia" :key="'e'+n" class="cal-sel-cell cal-sel-empty"></div>
                <div
                  v-for="dia in calDiasDelMes"
                  :key="dia"
                  class="cal-sel-cell"
                  :class="getDayClass(dia)"
                  @click="clickDia(dia)"
                  @mouseenter="hoverDay = dia"
                  @mouseleave="hoverDay = null"
                >
                  <span>{{ dia }}</span>
                </div>
              </div>

              <!-- Estado de selección -->
              <div class="d-flex align-center mt-3 pa-2 rounded" style="background:#f8f9fa;">
                <template v-if="clickState">
                  <v-icon small color="orange darken-1" class="mr-2">mdi-cursor-pointer</v-icon>
                  <span class="caption orange--text text--darken-1 font-weight-medium">
                    Inicio: {{ pendienteLabel }} — selecciona el día final del rango
                  </span>
                </template>
                <template v-else>
                  <v-icon x-small color="grey" class="mr-2">mdi-information-outline</v-icon>
                  <span class="caption grey--text">
                    1er clic = día inicio, 2do clic = día fin del rango. Mismo día = día único.
                  </span>
                </template>
              </div>
            </v-col>

            <!-- Panel fechas seleccionadas -->
            <v-col cols="12" md="5">
              <div class="d-flex align-center mb-3">
                <v-icon small color="grey darken-1" class="mr-2">mdi-calendar-check-outline</v-icon>
                <span class="caption font-weight-bold grey--text text--darken-2">
                  Fechas Seleccionadas
                </span>
                <v-badge
                  v-if="selecciones.length > 0"
                  :content="String(selecciones.length)"
                  color="green darken-1"
                  inline
                  class="ml-2"
                />
              </div>

              <div v-if="selecciones.length === 0" class="fechas-empty">
                <v-icon color="grey lighten-2" size="40">mdi-calendar-blank-outline</v-icon>
                <div class="caption grey--text mt-2">Selecciona días en el calendario</div>
              </div>

              <div
                v-for="(sel, i) in selecciones"
                :key="i"
                class="fecha-item"
                :class="sel.tipo === 'rango' ? 'fecha-item--rango' : 'fecha-item--dia'"
              >
                <div class="fecha-item-icon">
                  <v-icon small :color="sel.tipo === 'rango' ? 'green darken-2' : 'teal darken-1'">
                    {{ sel.tipo === 'rango' ? 'mdi-calendar-range' : 'mdi-calendar' }}
                  </v-icon>
                </div>
                <div class="fecha-item-body" style="flex:1;">
                  <template v-if="sel.tipo === 'rango'">
                    <div class="caption font-weight-bold">{{ formatFechaCard(sel.inicio) }}</div>
                    <div class="caption grey--text" style="font-size:10px;">hasta {{ formatFechaCard(sel.fin) }}</div>
                  </template>
                  <template v-else>
                    <div class="caption font-weight-bold">{{ formatFechaCard(sel.inicio) }}</div>
                    <div class="caption grey--text" style="font-size:10px;">Día único</div>
                  </template>
                  <div v-if="sel.creado_por || sel.actualizado_por" class="d-flex flex-wrap mt-1" style="gap:6px; font-size:10px;">
                    <span v-if="sel.creado_por" class="grey--text text--darken-1 d-flex align-center">
                      <v-icon style="font-size:10px;" class="mr-1" color="grey darken-1">mdi-account-plus-outline</v-icon>
                      {{ sel.creado_por }}<span v-if="sel.created_at" class="ml-1">({{ sel.created_at }})</span>
                    </span>
                    <span v-if="sel.actualizado_por" class="blue--text text--darken-1 d-flex align-center">
                      <v-icon style="font-size:10px;" class="mr-1" color="blue darken-1">mdi-account-edit-outline</v-icon>
                      {{ sel.actualizado_por }}<span v-if="sel.updated_at" class="ml-1">({{ sel.updated_at }})</span>
                    </span>
                  </div>
                </div>
                <v-btn icon x-small @click="removeSeleccion(i)">
                  <v-icon x-small color="grey lighten-1">mdi-close</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </div>

    <Conceptos ref="conceptosModal" @conceptos_sel="onConceptosSel" />

    <!-- Dialog consulta de precios -->
    <v-dialog v-model="preciosDialog" max-width="620" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center pa-4" style="background:#00695C;">
          <v-icon color="white" class="mr-2">mdi-currency-usd</v-icon>
          <span class="subtitle-1 font-weight-bold white--text">Consulta de Precios</span>
          <v-spacer />
          <v-btn icon small @click="preciosDialog = false"><v-icon color="white">mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div v-if="preciosDialogLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="teal darken-1" size="40" />
            <div class="caption grey--text mt-3">Consultando precios...</div>
          </div>
          <div v-else-if="preciosDialogData.length === 0" class="text-center py-8">
            <v-icon size="40" color="grey lighten-2">mdi-currency-usd-off</v-icon>
            <div class="caption grey--text mt-2">Sin resultados para los parámetros seleccionados</div>
          </div>
          <div v-else>
            <div
              v-for="(item, i) in preciosDialogData"
              :key="i"
              class="precio-item mb-3"
            >
              <div class="precio-item-header">
                <v-icon small color="white" class="mr-2">mdi-tag-outline</v-icon>
                <span class="subtitle-2 font-weight-bold white--text">{{ item.concepto }}</span>
              </div>
              <div class="precio-item-body">
                <div class="precio-dato">
                  <div class="precio-dato-label">Precio Base</div>
                  <div class="precio-dato-val">Bs {{ parseFloat(item.precio).toFixed(2) }}</div>
                </div>
                <div class="precio-dato">
                  <div class="precio-dato-label">Nº Cuotas</div>
                  <div class="precio-dato-val">{{ item.cuotas }}</div>
                </div>
                <div class="precio-dato precio-dato--orange">
                  <div class="precio-dato-label">Desc. Máximo</div>
                  <div class="precio-dato-val">{{ parseFloat(item.descuentoMaximo).toFixed(2) }}%</div>
                </div>
                <div class="precio-dato precio-dato--green">
                  <div class="precio-dato-label">Precio Mínimo</div>
                  <div class="precio-dato-val">Bs {{ parseFloat(item.precioMinimo).toFixed(2) }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn small depressed color="teal darken-1" dark @click="aplicarPrecios">
            <v-icon left small>mdi-check</v-icon> Aplicar precios a conceptos
          </v-btn>
          <v-btn small text @click="preciosDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog advertencia precio bajo mínimo -->
    <v-dialog v-model="dialogConfirmBajoMinimo" max-width="460" persistent>
      <v-card>
        <v-card-title class="orange darken-2 white--text py-3">
          <v-icon left color="white">mdi-alert-outline</v-icon>
          Precio bajo mínimo
        </v-card-title>
        <v-card-text class="pt-5 pb-2">
          <p>Existen conceptos cuyo <strong>precio final es menor al precio mínimo</strong> configurado.</p>
          <v-alert type="warning" dense outlined class="caption mb-3">
            Al confirmar, asumes la <strong>responsabilidad</strong> de guardar la promoción con precios por debajo del mínimo permitido.
          </v-alert>
          <p class="caption grey--text">¿Deseas continuar de todas formas?</p>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-btn text @click="dialogConfirmBajoMinimo = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="orange darken-2" dark depressed @click="dialogConfirmBajoMinimo = false; ejecutarGuardar()">
            <v-icon left small>mdi-check</v-icon>
            Confirmar y guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog análisis de similitud de áreas -->
    <v-dialog v-model="dialogAreasAnalisis" max-width="720" scrollable>
      <v-card>
        <v-card-title class="blue darken-3 white--text py-3">
          <v-icon left color="white">mdi-compare-horizontal</v-icon>
          Análisis de similitud — Áreas
        </v-card-title>
        <v-card-subtitle class="blue darken-3 white--text pb-3 pt-0 caption">
          GraphQL (sistema) vs REST (pagos.esam.edu.bo)
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pa-0" style="max-height:520px;">
          <v-simple-table dense>
            <thead>
              <tr style="background:#E3F2FD;">
                <th class="caption">Área GraphQL</th>
                <th class="caption">Similares en REST</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in areasAnalisis.filter(r => r.similares.some(s => s.score >= 80))" :key="i">
                <td class="caption font-weight-medium py-2" style="min-width:180px; border-right:1px solid #eee;">
                  {{ row.gql.nombre }}
                </td>
                <td class="py-2">
                  <div v-if="row.similares.filter(r => r.score >= 80).length === 0" class="caption grey--text">Sin coincidencias</div>
                  <div v-for="(r, j) in row.similares.filter(r => r.score >= 80)" :key="j" class="d-flex align-center mb-1" style="gap:6px;">
                    <v-chip x-small dark :color="r.score === 100 ? 'green darken-2' : 'teal'">
                      {{ r.score }}%
                    </v-chip>
                    <span class="caption">{{ r.nombre }}</span>
                  </div>
                </td>
              </tr>
              <!-- Áreas REST sin match en GraphQL -->
              <template v-if="areaOptionsRest.filter(r => !areasAnalisis.some(a => a.similares.find(s => s.id === r.id))).length > 0">
                <tr><td colspan="2" class="caption font-weight-bold orange--text text--darken-2 py-2 px-3">Solo en REST (sin equivalente en GraphQL)</td></tr>
                <tr v-for="(r, i) in areaOptionsRest.filter(r => !areasAnalisis.some(a => a.similares.find(s => s.id === r.id)))" :key="'solo'+i">
                  <td class="caption grey--text py-1">—</td>
                  <td class="caption py-1">{{ r.nombre }}</td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="dialogAreasAnalisis = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Overlay cargando plantilla -->
    <v-overlay :value="loadingTemplate" z-index="20" color="white" opacity="0.85">
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate color="blue darken-4" size="52" width="4" />
        <div class="subtitle-2 blue--text text--darken-4 mt-4 font-weight-bold">{{ editMode ? 'Cargando promoción...' : 'Cargando plantilla...' }}</div>
        <div class="caption grey--text mt-1">Obteniendo reglas y conceptos</div>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import Conceptos from '@/views/promociones/listar-conceptos.vue';
import xaxios from '@/xaxios';

import gql from 'graphql-tag';

export default {
  name: 'PromoCreateV3',
  components: { Conceptos },
  data() {
    return {
      nombre: '',
      estado: '',
      descripcion: '',
      saving: false,
      errorMsg: '',
      preciosDialog: false,
      dialogConfirmBajoMinimo: false,
      preciosDialogLoading: false,
      preciosDialogData: [],
      preciosDialogRi: null,
      // Opciones estáticas
      estadoOptions: [
        { text: 'No Aprobada', value: 0 },
        { text: 'Aprobada', value: 1 },
      ],
      estadoReglaOptions: [],
      tiposListaPrecios: [],
      planPagoOptions: [
        { text: 'Crédito', value: 'credito' },
        { text: 'Contado', value: 'contado' },
      ],
      modalidadOptions: [
        { text: 'Teórico',          value: 'teorico' },
        { text: 'Práctico',         value: 'practico' },
        //{ text: 'Teórico-Práctico', value: 'teorico_practico' },
      ],
      conceptosHeaders: [
        { text: 'Concepto',     value: 'nombre',      sortable: false },
        { text: 'Precio Base',  value: 'precioBase',  sortable: false, align: 'center', width: 110 },
        { text: 'Nº Cuotas',   value: 'cuotas',      sortable: false, align: 'center', width: 90 },
        { text: 'Descuento',    value: 'descuento',   sortable: false, align: 'center', width: 180 },
        { text: 'P. Mínimo',   value: 'precioMinimo', sortable: false, align: 'right',  width: 100 },
        { text: 'Precio Final', value: 'precioFinal', sortable: false, align: 'right',  width: 120 },
        { text: 'Valor Cuota', value: 'valorCuota',  sortable: false, align: 'right',  width: 120 },
        { text: '',             value: 'acciones',    sortable: false, align: 'center', width: 40 },
      ],
      // Unidad / Categorías / Áreas
      unidadSeleccionada: null,
      unidades: [],
      categoriaOptions: [],
      areaOptions: [],
      areaOptionsRest: [],
      loadingUnidades: false,
      loadingCategorias: false,
      loadingAreas: false,
      dialogAreasAnalisis: false,
      reglaAreasAnalisis: null,
      // Calendario
      calDate: new Date(),
      selecciones: [],
      clickState: null,
      hoverDay: null,
      // Reglas
      reglas: [],
      currentReglaIdx: null,
      vistaTabla: false,
      // Plantilla
      dialogPlantilla: false,
      plantillaNombre: '',
      loadingTemplate: false,
    };
  },
  computed: {
    editMode() { return !!this.$route.params.id; },
    promoId()  { return this.$route.params.id || null; },
    // ── Items para selects ──────────────────────────────────
    tipoPrecioOptions() {
      return this.tiposListaPrecios.map(t => ({ text: t.nombre, value: t.id }));
    },
    unidadesItems() {
      return this.unidades.map(u => ({ text: u.nombre, value: u.id }));
    },
    categoriasItems() {
      return this.categoriaOptions.map(c => ({ text: c.nombre, value: c.id }));
    },
    areasItems() {
      if (this.areaOptionsRest.length === 0) {
        return this.areaOptions.map(a => ({ text: a.nombre, value: a.id }));
      }
      const conMatch = this.areasAnalisis
        .filter(r => r.similares.some(s => s.score >= 80))
        .map(r => r.gql.id);
      return this.areaOptions
        .filter(a => conMatch.includes(a.id))
        .map(a => ({ text: a.nombre, value: a.id }));
    },
    areasAnalisis() {
      const normalizar = s => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      return this.areaOptions.map(gql => {
        const nGql = normalizar(gql.nombre);
        const similares = this.areaOptionsRest
          .map(r => {
            const nRest = normalizar(r.nombre);
            // similitud simple: contiene, o comparte palabras significativas
            const palabrasGql  = nGql.split(/\s+/).filter(w => w.length > 3);
            const palabrasRest = nRest.split(/\s+/).filter(w => w.length > 3);
            const coincidencias = palabrasGql.filter(w => palabrasRest.includes(w)).length;
            const score = nGql === nRest ? 100
              : nGql.includes(nRest) || nRest.includes(nGql) ? 80
              : palabrasGql.length > 0 ? Math.round((coincidencias / Math.max(palabrasGql.length, palabrasRest.length)) * 100)
              : 0;
            return { ...r, score };
          })
          .filter(r => r.score > 0)
          .sort((a, b) => b.score - a.score);
        return { gql, similares };
      });
    },
    // ── Estado de secciones ─────────────────────────────────
    sec1Ok() {
      return !!this.nombre.trim() && !!this.estado && !!this.unidadSeleccionada;
    },
    sec2Ok() {
      return this.selecciones.length > 0;
    },
    sec3Ok() {
      return this.reglas.length > 0;
    },
    // ── Calendario ─────────────────────────────────────────
    calMes() {
      return { y: this.calDate.getFullYear(), m: this.calDate.getMonth() };
    },
    calMesLabel() {
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio',
        'Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      return `${meses[this.calMes.m]} ${this.calMes.y}`;
    },
    calDiasDelMes() {
      const { y, m } = this.calMes;
      return Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => i + 1);
    },
    calPrimerDia() {
      const { y, m } = this.calMes;
      return (new Date(y, m, 1).getDay() + 6) % 7;
    },
    calHoy() {
      const t = new Date();
      const { y, m } = this.calMes;
      if (t.getFullYear() === y && t.getMonth() === m) return t.getDate();
      return null;
    },
    seleccionesConteo() {
      let n = 0;
      this.selecciones.forEach(sel => {
        if (sel.tipo === 'dia') { n++; return; }
        let d = new Date(sel.inicio);
        while (d <= sel.fin) { n++; d = new Date(d.getTime() + 86400000); }
      });
      return n;
    },
    pendienteLabel() {
      return this.clickState ? this.formatFechaCard(this.makeDate(this.clickState.dia)) : null;
    },
    tablaData() {
      if (this.reglas.length === 0) return null;

      const conceptosSet = new Set();
      this.reglas.forEach(r => {
        (r.conceptos || []).forEach(c => conceptosSet.add(c.nombre));
      });
      const conceptos = [...conceptosSet];

      const grupoMap = new Map();
      this.reglas.forEach((r, ri) => {
        const catNombre = this.labelCategoria(r.categoria) || 'Sin categoría';
        if (!grupoMap.has(catNombre)) {
          grupoMap.set(catNombre, { nombre: catNombre, filas: [] });
        }
        const conceptoMap = {};
        (r.conceptos || []).forEach(c => { conceptoMap[c.nombre] = c; });
        grupoMap.get(catNombre).filas.push({ ri, regla: r, conceptoMap });
      });

      return { conceptos, grupos: [...grupoMap.values()] };
    },
    tablaColumnasSub() {
      if (!this.tablaData) return [];
      const cols = [];
      this.tablaData.conceptos.forEach(c => {
        cols.push({ key: c+'_b', label: 'BASE',     cls: '' });
        cols.push({ key: c+'_d', label: 'DESC',     cls: 'xt-th-sub--desc' });
        cols.push({ key: c+'_n', label: 'Nº CUOTA', cls: '' });
        cols.push({ key: c+'_f', label: 'FINAL',    cls: 'xt-th-sub--final' });
        cols.push({ key: c+'_v', label: 'V. CUOTA', cls: 'xt-th-sub--cuota' });
      });
      cols.push({ key: '__sb', label: 'TOTAL BASE',  cls: 'xt-th-sub--s' });
      cols.push({ key: '__sf', label: 'TOTAL FINAL', cls: 'xt-th-sub--s xt-th-sub--final' });
      cols.push({ key: '__sa', label: 'AHORRO',      cls: 'xt-th-sub--s xt-th-sub--desc' });
      cols.push({ key: '__sc', label: '1RA CUOTA',   cls: 'xt-th-sub--s xt-th-sub--cuota' });
      return cols;
    },
  },
  methods: {
    // ── Helpers de label ─────────────────────────────────────
    estadoColor(estado) {
      const map = {
        // estado general de la promoción (numérico)
        0: 'red darken-1', 1: 'green darken-1',
        // estados de regla (códigos del API)
        EN_CURSO_INICIADO_EN_TIEMPO:               'green darken-2',
        EN_ARRASTRE_INICIADO_EN_ARRASTRE:          'orange darken-2',
        EN_ARRASTRE_INICIADO_SIN_PUNTO_EQUILIBRIO: 'deep-orange darken-2',
        EN_ARRASTRE_DESCARTADO_NO_EJECUTADO:       'grey darken-1',
        EN_CURSO_FINALIZADO_MODULO_0:              'green darken-1',
        EN_CURSO_FINALIZADO_DESARROLLO:            'teal darken-1',
        TITULADO:                                  'blue darken-2',
        EN_PROCESO_TITULACION:                     'blue',
        SE_QUEDA_EN_CULMINADO:                     'cyan darken-2',
        NO_PASO_ETAPA_TESTEO:                      'red darken-1',
        NO_ALCANZO_CANTIDAD_MINIMA:                'red',
        NO_DOCENTES_ESPECIALIZADOS:                'red lighten-1',
        PROBLEMAS_ORGANIZACION:                    'red darken-2',
      };
      return map[estado] || 'grey';
    },
    labelCategoria(id) {
      const c = this.categoriaOptions.find(x => x.id === id);
      return c ? c.nombre : null;
    },
    labelArea(id) {
      const a = this.areaOptions.find(x => x.id === id);
      return a ? a.nombre : null;
    },
    labelModalidad(val) {
      const map = { teorico: 'Teórico', practico: 'Práctico', teorico_practico: 'Teórico-Práctico' };
      return map[val] || val;
    },
    areaPermitePractico(areaId) {
      if (!areaId) return false;
      const area = this.areaOptions.find(a => a.id === areaId);
      if (!area) return false;
      const nombre = area.nombre.toLowerCase();
      return nombre.includes('salud') || nombre.includes('ingenier');
    },
    modalidadItemsParaRegla(regla) {
      return this.modalidadOptions.map(opt => ({
        ...opt,
        disabled: opt.value === 'practico' && !this.areaPermitePractico(regla.area),
      }));
    },
    onAreaChange(regla) {
      if (regla.modalidad === 'practico' && !this.areaPermitePractico(regla.area)) {
        regla.modalidad = '';
      }
    },
    labelTipoPrecio(val) {
      if (!val) return val;
      const found = this.tiposListaPrecios.find(t => t.id == val);
      return found ? found.nombre : val;
    },
    async consultarPrecios(ri) {
      const regla = this.reglas[ri];
      const cat  = this.categoriaOptions.find(x => x.id === regla.categoria);
      const area = this.areaOptions.find(x => x.id === regla.area);

      let areaNombre = area ? area.nombre : regla.area;
      if (area) {
        const analisis = this.areasAnalisis.find(r => r.gql.id === area.id);
        if (analisis && analisis.similares.length > 0 && analisis.similares[0].score > 50) {
          areaNombre = analisis.similares[0].nombre;
        }
      }

      this.preciosDialogRi = ri;
      this.preciosDialogData = [];
      this.preciosDialog = true;
      this.preciosDialogLoading = true;
      try {
        const { data } = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_URL_API_PAGOS}listas-precios/consultar`,
          params: {
            tipoListaPrecioId: regla.tipoPrecio,
            categoria:         cat  ? cat.nombre  : regla.categoria,
            area:              areaNombre,
            modalidad:         regla.modalidad,
            modalidadPago:     regla.planPago,
          },
        });
        this.preciosDialogData = data.datos || [];
      } catch (e) {
        window.console.error('Error consultando precios', e);
      } finally {
        this.preciosDialogLoading = false;
      }
    },
    aplicarPrecios() {
      const ri = this.preciosDialogRi;
      if (ri === null) return;
      const regla = this.reglas[ri];
      this.preciosDialogData.forEach(item => {
        const existing = regla.conceptos.find(c => c.nombre && c.nombre.toLowerCase() === item.concepto.toLowerCase());
        if (existing) {
          existing.precioBase   = parseFloat(item.precio)         || existing.precioBase;
          existing.cuotas       = item.cuotas                     || existing.cuotas;
          existing.precioMinimo = parseFloat(item.precioMinimo)   || 0;
        } else {
          regla.conceptos.push({
            id:           item.concepto_pago_id || item.id_concepto || item.conceptoPagoId ||item.conceptoId|| null,
            nombre:       item.concepto,
            precioBase:   parseFloat(item.precio)       || 0,
            cuotas:       item.cuotas                   || 1,
            descuento:    0,
            isPorcentaje: true,
            precioMinimo: parseFloat(item.precioMinimo) || 0,
          });
        }
      });
      this.$forceUpdate();
      this.preciosDialog = false;
    },
    resolverTipoPrecioId(val) {
      if (!val) return null;
      // Si ya es un número (id), devolverlo tal cual
      if (typeof val === 'number') return val;
      const numVal = Number(val);
      if (!isNaN(numVal) && numVal > 0) return numVal;
      // Buscar por coincidencia parcial en el nombre (case-insensitive)
      const lower = String(val).toLowerCase();
      const found = this.tiposListaPrecios.find(t => t.nombre.toLowerCase().includes(lower));
      return found ? found.id : null;
    },
    async loadTiposListaPrecios() {
      try {
        const { data } = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_URL_API_PAGOS}tipos-lista-precios`,
          params: { universidadId: 9 },
        });
        this.tiposListaPrecios = data.datos || [];
      } catch (e) {
        window.console.error('Error cargando tipos de lista de precios', e);
      }
    },
    async loadEstadosRegla() {
      try {
        const res = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/programa/estados`,
        });
        const raw = res.data;
        const list = raw.data || raw.datos || raw;
        if (!Array.isArray(list)) return;

        const parents  = list.filter(e => !e.parent_id);
        const children = list.filter(e => !!e.parent_id);

        const result = [];
        parents.forEach(parent => {
          const kids = children.filter(c => c.parent_id === parent.id);
          if (kids.length) {
            result.push({ header: parent.nombre });
            kids.forEach(k => result.push({ text: k.nombre, value: k.codigo, id: k.id }));
          } else {
            result.push({ text: parent.nombre, value: parent.codigo, id: parent.id });
          }
        });

        this.estadoReglaOptions = result;
      } catch (e) {
        window.console.error('Error cargando estados de regla', e);
      }
    },
    getCeldasFila(fila) {
      const celdas = [];
      this.tablaData.conceptos.forEach(cNombre => {
        const c = fila.conceptoMap[cNombre];
        if (c) {
          const desc = c.descuento || 0;
          celdas.push({ key: cNombre+'_b', cls: 'xt-td xt-td-num',   val: (c.precioBase || 0).toFixed(2) });
          celdas.push({ key: cNombre+'_d', cls: 'xt-td xt-td-desc',  val: desc > 0 ? (desc + (c.isPorcentaje ? '%' : ' Bs')) : null, zero: desc <= 0 });
          celdas.push({ key: cNombre+'_n', cls: 'xt-td xt-td-num',   val: c.cuotas || 1 });
          celdas.push({ key: cNombre+'_f', cls: 'xt-td xt-td-final', val: this.pFinal(c).toFixed(2) });
          celdas.push({ key: cNombre+'_v', cls: 'xt-td xt-td-cuota', val: this.vCuota(c).toFixed(2) });
        } else {
          celdas.push({ key: cNombre+'_e', cls: 'xt-td xt-td-empty', val: '—', colspan: 5 });
        }
      });
      celdas.push({ key: '__sb', cls: 'xt-td xt-td-num xt-td-s',                   val: this.tBase(fila.regla).toFixed(2) });
      celdas.push({ key: '__sf', cls: 'xt-td xt-td-final xt-td-s xt-td-s--final',  val: this.tFinal(fila.regla).toFixed(2) });
      celdas.push({ key: '__sa', cls: 'xt-td xt-td-desc xt-td-s xt-td-s--desc',    val: this.tAhorro(fila.regla).toFixed(2) });
      celdas.push({ key: '__sc', cls: 'xt-td xt-td-cuota xt-td-s xt-td-s--cuota',  val: this.tPrimeraCuota(fila.regla).toFixed(2) });
      return celdas;
    },

    // ── Calendario ──────────────────────────────────────────
    prevMonthCal() {
      const { y, m } = this.calMes;
      this.calDate = new Date(y, m - 1, 1);
      this.clickState = null;
    },
    nextMonthCal() {
      const { y, m } = this.calMes;
      this.calDate = new Date(y, m + 1, 1);
      this.clickState = null;
    },
    makeDate(dia) {
      const { y, m } = this.calMes;
      return new Date(y, m, dia);
    },
    clickDia(dia) {
      if (!this.clickState) {
        this.clickState = { dia };
      } else {
        const d1 = this.makeDate(this.clickState.dia);
        const d2 = this.makeDate(dia);
        if (d1.getTime() === d2.getTime()) {
          this.selecciones.push({ tipo: 'dia', inicio: new Date(d1), fin: new Date(d1) });
        } else {
          const inicio = d1 < d2 ? d1 : d2;
          const fin   = d1 < d2 ? d2 : d1;
          this.selecciones.push({ tipo: 'rango', inicio, fin });
        }
        this.clickState = null;
        this.hoverDay = null;
      }
    },
    getDayClass(dia) {
      const d = this.makeDate(dia);
      const classes = [];
      if (dia === this.calHoy) classes.push('cal-day--hoy');

      // Preview del rango mientras se hace hover con primer clic hecho
      if (this.clickState && this.hoverDay) {
        const d1 = this.makeDate(this.clickState.dia);
        const dH = this.makeDate(this.hoverDay);
        const ini = d1 < dH ? d1 : dH;
        const fin = d1 < dH ? dH : d1;
        if (dia === this.clickState.dia) { classes.push('cal-day--selecting'); return classes; }
        if (d > ini && d < fin) { classes.push('cal-day--hover-range'); return classes; }
        if (d.getTime() === fin.getTime() && dia !== this.clickState.dia) { classes.push('cal-day--hover-end'); return classes; }
      } else if (this.clickState && this.clickState.dia === dia) {
        classes.push('cal-day--selecting');
        return classes;
      }

      for (const sel of this.selecciones) {
        if (sel.tipo === 'dia' && d.getTime() === sel.inicio.getTime()) { classes.push('cal-day--selected'); return classes; }
        if (sel.tipo === 'rango') {
          if (d.getTime() === sel.inicio.getTime()) { classes.push('cal-day--range-start'); return classes; }
          if (d.getTime() === sel.fin.getTime())   { classes.push('cal-day--range-end');   return classes; }
          if (d > sel.inicio && d < sel.fin)        { classes.push('cal-day--in-range');    return classes; }
        }
      }
      return classes;
    },
    removeSeleccion(i) { this.selecciones.splice(i, 1); },
    formatFechaCard(date) {
      const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return `${date.getDate()} ${meses[date.getMonth()]} ${date.getFullYear()}`;
    },

    // ── Unidad / Categorías / Áreas ─────────────────────────
    async loadUnidades() {
      this.loadingUnidades = true;
      try {
        const res = await this.$apollo.query({
          query: gql`query { NegociosAll { id nombre } }`,
        });
        this.unidades = res.data.NegociosAll || [];
      } catch (e) {
        window.console.error(e);
      } finally {
        this.loadingUnidades = false;
      }
    },
    onUnidadChange(id) {
      this.categoriaOptions = [];
      this.areaOptions = [];
      this.reglas.forEach(r => { r.categoria = null; r.area = null; });
      if (!id) return;
      this.loadCategorias(id);
      this.loadAreas(id);
    },
    async loadCategorias(id) {
      this.loadingCategorias = true;
      try {
        const res = await this.$apollo.query({
          query: gql`query($id: Int!) { getCategorias(unidad_negocio_id: $id) { id nombre } }`,
          variables: { id },
          fetchPolicy: 'network-only',
        });
        this.categoriaOptions = res.data.getCategorias || [];
      } catch (e) {
        window.console.error(e);
      } finally {
        this.loadingCategorias = false;
      }
    },
    async loadAreas(id) {
      this.loadingAreas = true;
      try {
        const [gqlRes, restRes] = await Promise.allSettled([
          this.$apollo.query({
            query: gql`query($id: Int!) { getAreas(unidad_negocio_id: $id) { id nombre } }`,
            variables: { id },
            fetchPolicy: 'network-only',
          }),
          xaxios.request({
            method: 'get',
            url: `${process.env.VUE_APP_URL_API_PAGOS}areas-universidades`,
            params: { universidadId: 9 },
          }),
        ]);

        const fromGql = gqlRes.status === 'fulfilled' ? (gqlRes.value.data.getAreas || []) : [];
        const fromRest = restRes.status === 'fulfilled'
          ? (restRes.value.data?.datos || restRes.value.data || []).map(a => ({ id: a.id, nombre: a.nombre || a.area || a.descripcion || String(a.id) }))
          : [];

        this.areaOptions     = fromGql;
        this.areaOptionsRest = fromRest;
      } catch (e) {
        window.console.error(e);
      } finally {
        this.loadingAreas = false;
      }
    },

    // ── Reglas ──────────────────────────────────────────────
    agregarRegla() {
      this.reglas.push({
        categoria: null, area: null,
        estado: 'Activo', estadoPrograma: [], tipoPrecio: null, planPago: '', modalidad: '',
        conceptos: [],
      });
    },
    eliminarRegla(ri) { this.reglas.splice(ri, 1); },
    duplicarRegla(ri) {
      const original = this.reglas[ri];
      const copia = {
        categoria: original.categoria,
        area: original.area,
        estado: original.estado,
        estadoPrograma: [...(original.estadoPrograma || [])],
        tipoPrecio: original.tipoPrecio,
        planPago: original.planPago,
        modalidad: original.modalidad,
        conceptos: original.conceptos.map(c => ({ ...c })),
      };
      this.reglas.splice(ri + 1, 0, copia);
    },
    abrirConceptos(ri) {
      this.currentReglaIdx = ri;
      this.$refs.conceptosModal.openmodal(null);
    },
    onConceptosSel(items) {
      if (this.currentReglaIdx === null) return;
      const regla = this.reglas[this.currentReglaIdx];
      const existingIds = regla.conceptos.map(c => c.id);
      items.forEach(item => {
        if (!existingIds.includes(item.id)) {
          regla.conceptos.push({ id: item.id, nombre: item.nombre, precioBase: 0, cuotas: 1, descuento: 0, isPorcentaje: true });
        }
      });
      this.currentReglaIdx = null;
    },
    quitarConcepto(ri, ci) { this.reglas[ri].conceptos.splice(ci, 1); },

    // ── Cálculos ────────────────────────────────────────────
    pFinal(con) {
      const base = con.precioBase || 0;
      const desc = con.descuento || 0;
      const calc = con.isPorcentaje ? base * (1 - desc / 100) : Math.max(base - desc, 0);
      return Math.max(calc, 0);
    },
    maxDescuentoPct(con) {
      const base = con.precioBase || 0;
      const minimo = parseFloat(con.precioMinimo) || 0;
      if (base <= 0) return 100;
      if (minimo <= 0) return 100;
      return parseFloat((((base - minimo) / base) * 100).toFixed(2));
    },
    onDescuentoInput(item, val) {
      item.descuento = parseFloat(val) || 0;
      this.$forceUpdate();
    },
    descuentoSobreLimite(item) {
      if (!item.precioMinimo) return false;
      if (item.isPorcentaje) {
        return item.descuento > this.maxDescuentoPct(item);
      } else {
        const maxDesc = Math.max((item.precioBase || 0) - (parseFloat(item.precioMinimo) || 0), 0);
        return item.descuento > maxDesc;
      }
    },
    clampDescuento(item) {
      if (!this.descuentoSobreLimite(item)) return;
      if (item.isPorcentaje) {
        item.descuento = this.maxDescuentoPct(item);
      } else {
        item.descuento = Math.max((item.precioBase || 0) - (parseFloat(item.precioMinimo) || 0), 0);
      }
      this.$forceUpdate();
    },
    conceptosBajoMinimo(regla) {
      return regla.conceptos.filter(c => c.precioMinimo > 0 && this.pFinal(c) < c.precioMinimo);
    },
    vCuota(con)          { return this.pFinal(con) / Math.max(con.cuotas || 1, 1); },
    tBase(regla)         { return regla.conceptos.reduce((a, c) => a + (c.precioBase || 0), 0); },
    tFinal(regla)        { return regla.conceptos.reduce((a, c) => a + this.pFinal(c), 0); },
    tAhorro(regla)       { return this.tBase(regla) - this.tFinal(regla); },
    tPrimeraCuota(regla) { return regla.conceptos.reduce((a, c) => a + this.vCuota(c), 0); },

    // ── Plantillas ───────────────────────────────────────────
    async saveAsTemplate() {
      const nombre = this.plantillaNombre.trim();
      if (!nombre) return;

      const DATA_USER  = this.$store.state.DATA_USER;
      const creado_por = DATA_USER
        ? `${DATA_USER.nombres || ''} ${DATA_USER.pri_apellido || ''} ${DATA_USER.seg_apellido || ''}`.trim()
        : '';

      const payload = {
        nombre,
        descripcion:       this.descripcion || '',
        estado:            this.estado,
        is_template:       true,
        creado_por,
        unidad_negocio_id: this.unidadSeleccionada || null,
        fechas: [],
        reglas: this.reglas.map(r => {
          const tipoPrecioText = this.labelTipoPrecio(r.tipoPrecio);
          const estadoProgramaIds = (r.estadoPrograma || []).map(val => {
            const o = this.estadoReglaOptions.find(o => o.value === val);
            return o ? o.id : null;
          }).filter(Boolean);
          return {
            categoria_id:        r.categoria     || null,
            area_id:             r.area          || null,
            tipo_precio:         tipoPrecioText  || null,
            plan_pago:           r.planPago      || null,
            estado:              r.estado        || 'Activo',
            estado_programa_ids: estadoProgramaIds.length ? estadoProgramaIds : null,
            modalidad:           r.modalidad     || null,
            conceptos: r.conceptos
              .filter(c => c.id)
              .map(c => ({
                concepto_pago_id: c.id,
                precio_base:      c.precioBase  || 0,
                cuotas:           c.cuotas      || 1,
                is_porcentaje:    c.isPorcentaje,
                valor_descuento:  c.descuento   || 0,
              })),
          };
        }),
      };

      try {
        await xaxios.request({
          method: 'post',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3`,
          data: payload,
        });
        // También guardar en localStorage para acceso rápido en el selector
        const templates = JSON.parse(localStorage.getItem('promo_v3_templates') || '[]');
        templates.push({
          id: Date.now().toString(),
          nombre,
          descripcion:       this.descripcion,
          unidad_negocio_id: this.unidadSeleccionada,
          reglas:            JSON.parse(JSON.stringify(this.reglas)),
          createdAt:         new Date().toISOString(),
        });
        localStorage.setItem('promo_v3_templates', JSON.stringify(templates));
        this.dialogPlantilla = false;
        this.plantillaNombre = '';
        this.$notify({ group: 'auth', type: 'success', title: 'Plantilla guardada', text: 'Disponible al crear la próxima promoción.' });
      } catch {
        this.$notify({ group: 'auth', type: 'error', title: 'Error', text: 'No se pudo guardar la plantilla.' });
      }
    },
    async loadFromTemplate() {
      const raw = localStorage.getItem('promo_v3_template_pending');
      if (!raw) return;
      this.loadingTemplate = true;
      try {
        localStorage.removeItem('promo_v3_template_pending');
        const t = JSON.parse(raw);

        if (t.source === 'api') {
          // Cargar datos completos desde el backend
          const res = await xaxios.request({
            method: 'get',
            url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3/${t.id}`,
          });
          const data = res.data.data || res.data;
          if (data.descripcion)       this.descripcion        = data.descripcion;
          if (data.estado)            this.estado             = data.estado;
          if (data.nombre)            this.nombre             = data.nombre;
          if (data.unidad_negocio_id) {
            this.unidadSeleccionada = data.unidad_negocio_id;
            await this.loadCategorias(data.unidad_negocio_id);
            await this.loadAreas(data.unidad_negocio_id);
          }
          if (data.reglas) {
            // Obtener nombres de conceptos desde el API
            let conceptoMap = {};
            if (data.unidad_negocio_id) {
              try {
                const resC = await xaxios.request({
                  method: 'post',
                  url: `${process.env.VUE_APP_API_REST}/api/pagos/conceptos/buscar`,
                  data: { es_nacional: false, unidad_negocio_id: data.unidad_negocio_id },
                });
                (resC.data || []).forEach(c => { conceptoMap[c.id] = c.nombre; });
              } catch { /* si falla, los nombres quedan vacíos */ }
            }
            this.reglas = data.reglas.map(r => {
              const eIds = r.estado_programa_ids?.length ? r.estado_programa_ids : (r.estado_programa_id ? [r.estado_programa_id] : []);
              const estadosRegla = eIds.map(id => { const o = this.estadoReglaOptions.find(o => o.id === id); return o ? o.value : null; }).filter(Boolean);
              return {
              categoria:      r.categoria_id || null,
              area:           r.area_id      || null,
              tipoPrecio:     this.resolverTipoPrecioId(r.tipo_precio),
              planPago:       r.plan_pago    || '',
              estado:         r.estado       || 'Activo',
              estadoPrograma: estadosRegla,
              modalidad:      r.modalidad    || '',
              conceptos: (r.conceptos || []).map(c => ({
                id:           c.concepto_pago_id,
                nombre:       conceptoMap[c.concepto_pago_id] || c.nombre_concepto || `Concepto #${c.concepto_pago_id}`,
                precioBase:   parseFloat(c.precio_base)     || 0,
                cuotas:       c.cuotas                      || 1,
                descuento:    parseFloat(c.valor_descuento) || 0,
                isPorcentaje: c.is_porcentaje               !== false,
              })),
            };
            });
          }
        } else {
          // Template local
          if (t.descripcion)       this.descripcion        = t.descripcion;
          if (t.unidad_negocio_id) {
            this.unidadSeleccionada = t.unidad_negocio_id;
            await this.loadCategorias(t.unidad_negocio_id);
            await this.loadAreas(t.unidad_negocio_id);
          }
          this.reglas = t.reglas || [];
        }
      } catch (e) {
        window.console.error('loadFromTemplate error:', e);
      } finally {
        this.loadingTemplate = false;
      }
    },

    // ── Guardar ─────────────────────────────────────────────
    async loadForEdit(id) {
      this.loadingTemplate = true;
      try {
        const res = await xaxios.request({
          method: 'get',
          url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3/${id}`,
        });
        // El API puede devolver { data: {...} }, { datos: {...} } o el objeto directo
        const raw = res.data;
        const data = raw.data || raw.datos || raw;

        window.console.log('[loadForEdit] raw:', raw);
        window.console.log('[loadForEdit] data:', data);

        if (!data || !data.id) {
          this.errorMsg = 'No se encontraron datos de la promoción (id: ' + id + ')';
          return;
        }

        this.nombre      = data.nombre      || '';
        this.descripcion = data.descripcion || '';
        this.estado      = data.estado != null ? Number(data.estado) : null;

        if (data.unidad_negocio_id) {
          this.unidadSeleccionada = data.unidad_negocio_id;
          await this.loadCategorias(data.unidad_negocio_id);
          await this.loadAreas(data.unidad_negocio_id);
        }

        // Cargar fechas → selecciones
        if (data.fechas && data.fechas.length) {
          this.selecciones = data.fechas.map(f => {
            const ini = new Date(f.fecha_inicio + 'T00:00:00');
            const fin = new Date(f.fecha_fin   + 'T00:00:00');
            return {
              tipo: f.fecha_inicio === f.fecha_fin ? 'dia' : 'rango',
              inicio: ini, fin,
              creado_por:     f.creado_por     || null,
              created_at:     f.created_at     || null,
              actualizado_por: f.actualizado_por || null,
              updated_at:     f.updated_at     || null,
            };
          });
          this.calDate = new Date(data.fechas[0].fecha_inicio + 'T00:00:00');
        }

        // Cargar reglas
        if (data.reglas && data.reglas.length) {
          let conceptoMap = {};
          if (data.unidad_negocio_id) {
            try {
              const resC = await xaxios.request({
                method: 'post',
                url: `${process.env.VUE_APP_API_REST}/api/pagos/conceptos/buscar`,
                data: { es_nacional: false, unidad_negocio_id: data.unidad_negocio_id },
              });
              (resC.data || []).forEach(c => { conceptoMap[c.id] = c.nombre; });
            } catch { /* nombres quedan vacíos */ }
          }
          this.reglas = data.reglas.map(r => {
            const eIds2 = r.estado_programa_ids?.length ? r.estado_programa_ids : (r.estado_programa_id ? [r.estado_programa_id] : []);
            const estadosRegla2 = eIds2.map(id => { const o = this.estadoReglaOptions.find(o => o.id === id); return o ? o.value : null; }).filter(Boolean);
            return {
            categoria:      r.categoria_id || null,
            area:           r.area_id      || null,
            tipoPrecio:     this.resolverTipoPrecioId(r.tipo_precio),
            planPago:       r.plan_pago    || '',
            estado:         r.estado       || 'Activo',
            estadoPrograma: estadosRegla2,
            modalidad:      r.modalidad    || '',
            conceptos: (r.conceptos || []).map(c => ({
              id:           c.concepto_pago_id,
              nombre:       conceptoMap[c.concepto_pago_id] || c.nombre_concepto || `Concepto #${c.concepto_pago_id}`,
              precioBase:   parseFloat(c.precio_base)     || 0,
              cuotas:       c.cuotas                      || 1,
              descuento:    parseFloat(c.valor_descuento) || 0,
              isPorcentaje: c.is_porcentaje               !== false,
              precioMinimo: parseFloat(c.precio_minimo)   || 0,
            })),
          };
          });
        }
      } catch (e) {
        window.console.error('loadForEdit error:', e);
        this.errorMsg = 'No se pudo cargar la promoción para editar.';
      } finally {
        this.loadingTemplate = false;
      }
    },

    guardar() {
      this.errorMsg = '';
      if (!this.nombre.trim())      { this.errorMsg = 'El nombre de la promoción es requerido.'; return; }
      if (this.estado === null || this.estado === undefined || this.estado === '') { this.errorMsg = 'El estado es requerido.'; return; }
      if (!this.selecciones.length) { this.errorMsg = 'Selecciona al menos una fecha de disponibilidad.'; return; }
      if (!this.reglas.length)      { this.errorMsg = 'Agrega al menos una regla de descuento.'; return; }

      const hayBajoMinimo = this.reglas.some(r => this.conceptosBajoMinimo(r).length > 0);
      if (hayBajoMinimo) { this.dialogConfirmBajoMinimo = true; return; }

      this.ejecutarGuardar();
    },
    async ejecutarGuardar() {

      const DATA_USER  = this.$store.state.DATA_USER;
      const creado_por = DATA_USER
        ? `${DATA_USER.nombres || ''} ${DATA_USER.pri_apellido || ''} ${DATA_USER.seg_apellido || ''}`.trim()
        : '';

      const payload = {
        nombre:            this.nombre.trim(),
        descripcion:       this.descripcion || '',
        // estado: isNaN(this.estado) ? this.estado : 'Activo',


        estado:            this.estado,
        is_template:       false,
        creado_por,
        unidad_negocio_id: this.unidadSeleccionada || null,
        fechas: this.selecciones.map(sel => ({
          fecha_inicio: sel.inicio.toISOString().split('T')[0],
          fecha_fin:    sel.fin.toISOString().split('T')[0],
        })),
        reglas: this.reglas.map(r => {
          const tipoPrecioText = this.labelTipoPrecio(r.tipoPrecio);
          const estadoProgramaIds = (r.estadoPrograma || []).map(val => {
            const o = this.estadoReglaOptions.find(o => o.value === val);
            return o ? o.id : null;
          }).filter(Boolean);
          return {
            categoria_id:        r.categoria     || null,
            area_id:             r.area          || null,
            tipo_precio:         tipoPrecioText   || null,
            plan_pago:           r.planPago       || null,
            estado:              r.estado         || 'Activo',
            estado_programa_ids: estadoProgramaIds.length ? estadoProgramaIds : null,
            modalidad:          r.modalidad     || null,
            conceptos: r.conceptos
              .filter(c => c.nombre)
              .map(c => ({
                concepto_pago_id: c.id || null,
                precio_base:      c.precioBase   || 0,
                cuotas:           c.cuotas       || 1,
                is_porcentaje:    c.isPorcentaje,
                valor_descuento:  c.descuento    || 0,
              })),
          };
        }),
      };

      this.saving = true;
      try {
        if (this.editMode) {
          await xaxios.request({
            method: 'put',
            url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3/${this.promoId}`,
            data: payload,
          });
          this.$notify({ group: 'auth', type: 'success', title: 'Promoción actualizada', text: 'Los cambios se guardaron correctamente.' });
          setTimeout(() => this.$router.push(`/promociones-v3/${this.promoId}`), 1500);
        } else {
          await xaxios.request({
            method: 'post',
            url: `${process.env.VUE_APP_API_REST}/api/v1/esam/promociones/v3`,
            data: payload,
          });
          this.$notify({ group: 'auth', type: 'success', title: 'Promoción guardada', text: 'La promoción se creó correctamente.' });
          setTimeout(() => this.$router.push('/promociones-v3'), 1500);
        }
      } catch (e) {
        const detalle = e?.response?.data?.message || e?.response?.data?.error || e?.message || '';
        const texto = detalle
          ? `Error al guardar la promoción: ${detalle}`
          : 'Error al guardar la promoción. Verifica los datos e intenta nuevamente.';
        this.errorMsg = texto;
        this.$notify({ group: 'auth', type: 'error', title: 'Error al guardar', text: texto });
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.loadUnidades();
    await Promise.all([this.loadTiposListaPrecios(), this.loadEstadosRegla()]);
    if (this.editMode) {
      await this.loadForEdit(this.promoId);
    } else {
      await this.loadFromTemplate();
    }
  },
};
</script>

<style scoped>
/* ── Página ─────────────────────────────────── */
.cv3-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f5f5f5;
}

/* ── Top bar ────────────────────────────────── */
.cv3-topbar {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 4px;
}

/* Indicadores de pasos */
.cv3-steps {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 12px;
}
.cv3-step {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 12px;
  transition: all 0.2s;
}
.cv3-step--pending { color: #9e9e9e; }
.cv3-step--ok      { color: #2e7d32; background: #E8F5E9; }

/* ── Cuerpo ─────────────────────────────────── */
.cv3-body {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 32px 60px;
}

/* ── Section cards ──────────────────────────── */
.section-card { transition: border-color 0.25s; }
.section-card--ok { border-color: #4CAF50 !important; }

.section-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
}
.section-num {
  width: 28px;
  height: 28px;
  background: #1565c0;
  color: white;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Field label ────────────────────────────── */
.field-label {
  font-size: 11px;
  font-weight: 700;
  color: #757575;
  letter-spacing: 0.4px;
}

/* ── Calendario ─────────────────────────────── */
.cal-sel-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}
.cal-sel-head {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  padding: 4px 0 8px;
}
.cal-sel-cell {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}
.cal-sel-cell:hover               { background: #eeeeee; }
.cal-sel-empty                    { cursor: default; color: #ccc; }
.cal-sel-empty:hover              { background: transparent; }
/* Hoy */
.cal-day--hoy                     { border: 2px solid #1976d2; color: #1976d2; font-weight: 700; }
/* Seleccionado / rangos */
.cal-day--selected,
.cal-day--range-start,
.cal-day--range-end               { background: #4CAF50 !important; color: white !important; font-weight: 700; }
.cal-day--in-range                { background: #E8F5E9 !important; color: #1B5E20 !important; border-radius: 0; }
.cal-day--selecting               { background: #2E7D32 !important; color: white !important; }
/* Preview hover range */
.cal-day--hover-range             { background: #C8E6C9 !important; color: #2E7D32 !important; border-radius: 0; }
.cal-day--hover-end               { background: #81C784 !important; color: white !important; }

/* ── Fechas panel ───────────────────────────── */
.fechas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
}
.fecha-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid;
  margin-bottom: 6px;
}
.fecha-item--rango { background: #F1F8E9; border-color: #C8E6C9; }
.fecha-item--dia   { background: #E0F2F1; border-color: #B2DFDB; }
.fecha-item-icon   { flex-shrink: 0; }
.fecha-item-body   { flex: 1; min-width: 0; }

/* ── Regla card ─────────────────────────────── */
.regla-card { border-left: 4px solid #1565c0 !important; }
.regla-hdr {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}

/* ── Conceptos table ────────────────────────── */
.conceptos-tbl {
  border: 1px solid #eeeeee;
  border-radius: 4px;
}

/* ── Totales ────────────────────────────────── */
.regla-totals {
  display: flex;
  min-height: 68px;
}
.rtot-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 16px;
}
.rtot-cell--green { background: #F1F8E9; }
.rtot-cell--blue  { background: #E3F2FD; }
.rtot-cell--dark  { background: #212121; border-radius: 0 0 3px 0; }

.rtot-label       { font-size: 10px; font-weight: 700; color: #888; letter-spacing: 0.3px; display:flex; align-items:center; }
.rtot-label--green{ color: #2e7d32; }
.rtot-label--blue { color: #1565c0; }
.rtot-label--light{ color: rgba(255,255,255,.7); }

.rtot-value       { font-size: 14px; font-weight: 700; color: #333; margin-top: 3px; }
.rtot-value--lg   { font-size: 18px; }
.rtot-value--green{ color: #2e7d32; }
.rtot-value--blue { color: #1565c0; }
.rtot-value--white{ color: #fff; }

/* ── Agregar otra regla ─────────────────────── */
.add-otra-regla {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-bottom: 24px;
  transition: background 0.15s, border-color 0.15s;
}
.add-otra-regla:hover {
  background: #fafafa;
  border-color: #bdbdbd;
}

/* ── Toggle descuento ───────────────────────── */
.desc-toggle {
  display: flex;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.desc-toggle-btn {
  padding: 0 10px;
  height: 32px;
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  background: #fff;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  line-height: 1;
}
.desc-toggle-btn:first-child {
  border-right: 1px solid #d0d0d0;
}
.desc-toggle-btn:hover:not(.desc-toggle-btn--active) {
  background: #f5f5f5;
}
.desc-toggle-btn--active {
  background: #1565c0;
  color: #fff;
}

/* ── Vista Tabla ────────────────────────────── */
.xtabla-cat-hdr {
  display: flex;
  align-items: center;
  background: #1565c0;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 7px 14px;
  border-radius: 4px 4px 0 0;
}
.xtabla-scroll {
  overflow-x: auto;
  border: 1px solid #cfd8dc;
  border-top: none;
  border-radius: 0 0 4px 4px;
}
.xtabla {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
  background: #fff;
}
.xt-th {
  padding: 6px 10px;
  border: 1px solid #b0bec5;
  text-align: center;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.4px;
  white-space: nowrap;
}
.xt-th-area {
  background: #263238;
  color: #fff;
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 110px;
  text-align: left;
}
.xt-th-meta {
  background: #37474f;
  color: #eceff1;
  position: sticky;
  left: 110px;
  z-index: 3;
  min-width: 90px;
  font-size: 9px;
  line-height: 1.3;
}
.xt-th-group {
  background: #c8a200;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  border-bottom: none;
}
.xt-th-group--summary { background: #1565c0; }
.xt-th-sub {
  background: #fff8e1;
  color: #5d4037;
  font-size: 9px;
  padding: 4px 8px;
  border-top: 2px solid #c8a200;
}
.xt-th-sub--desc  { background: #fff3e0; color: #e65100; border-top-color: #c8a200; }
.xt-th-sub--final { background: #e8f5e9; color: #1b5e20; border-top-color: #c8a200; }
.xt-th-sub--cuota { background: #e3f2fd; color: #0d47a1; border-top-color: #c8a200; }
.xt-th-sub--s { background: #e8eaf6; color: #1a237e; border-top: 2px solid #1565c0; }
.xt-th-sub--s.xt-th-sub--final { background: #e8f5e9; color: #1b5e20; border-top-color: #1565c0; }
.xt-th-sub--s.xt-th-sub--desc  { background: #fff3e0; color: #e65100; border-top-color: #1565c0; }
.xt-th-sub--s.xt-th-sub--cuota { background: #e3f2fd; color: #0d47a1; border-top-color: #1565c0; }
.xt-td {
  padding: 5px 10px;
  border: 1px solid #dde2e6;
  white-space: nowrap;
  vertical-align: middle;
}
.xt-td-area {
  position: sticky;
  left: 0;
  background: #f5f5f5;
  font-weight: 700;
  font-size: 12px;
  color: #212121;
  min-width: 110px;
  z-index: 2;
  border-right: 2px solid #90a4ae;
}
.xt-td-meta {
  position: sticky;
  left: 110px;
  background: #fafafa;
  z-index: 2;
  min-width: 90px;
  border-right: 2px solid #90a4ae;
}
.xt-td-num  { text-align: right; color: #424242; }
.xt-td-desc { text-align: center; color: #e65100; font-weight: 600; }
.xt-td-final{ text-align: right; color: #2e7d32; font-weight: 700; }
.xt-td-cuota{ text-align: right; color: #1565c0; font-weight: 700; }
.xt-td-empty{ text-align: center; color: #bdbdbd; }
.xt-td-s        { border-left: 2px solid #90a4ae; background: #f5f5f5; }
.xt-td-s--final { background: #f1f8e9; color: #1b5e20 !important; }
.xt-td-s--desc  { background: #fff8f0; color: #e65100 !important; }
.xt-td-s--cuota { background: #e8f4fd; color: #0d47a1 !important; }
.xt-tr-even .xt-td-area,
.xt-tr-even .xt-td-meta { background: #f5f5f5; }
.xt-tr-odd  .xt-td-area,
.xt-tr-odd  .xt-td-meta { background: #eeeeee; }
.xt-tr-even { background: #fff; }
.xt-tr-odd  { background: #f9f9f9; }
.xt-tr-even:hover,
.xt-tr-odd:hover { background: #e8f4fb; }
.xt-tr-even:hover .xt-td-area,
.xt-tr-odd:hover  .xt-td-area,
.xt-tr-even:hover .xt-td-meta,
.xt-tr-odd:hover  .xt-td-meta { background: #d1ecf9; }
.xt-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  margin-bottom: 2px;
  white-space: nowrap;
}
.xt-badge--purple { background: #ede7f6; color: #4a148c; }
.xt-badge--orange { background: #fff3e0; color: #e65100; }
.xt-badge--brown  { background: #efebe9; color: #4e342e; }
.xt-zero { color: #bdbdbd; font-weight: 400; }

/* ── Descuento en límite / sobre límite ─────── */
.desc-at-limit ::v-deep .v-input__slot fieldset {
  border-color: #F57C00 !important;
  border-width: 2px !important;
}
.desc-at-limit ::v-deep input {
  color: #F57C00 !important;
  font-weight: 600;
}
.desc-over-limit ::v-deep .v-input__slot fieldset {
  border-color: #c62828 !important;
  border-width: 2px !important;
}
.desc-over-limit ::v-deep input {
  color: #c62828 !important;
  font-weight: 600;
}
.desc-over-msg {
  font-size: 10px;
  color: #c62828;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 2px;
  line-height: 1.2;
}

/* ── Dialog precios ─────────────────────────── */
.precio-item { border-radius: 8px; overflow: hidden; border: 1px solid #e0e0e0; }
.precio-item-header {
  display: flex; align-items: center;
  background: #00695C; padding: 8px 14px;
}
.precio-item-body {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 0;
}
.precio-dato {
  padding: 10px 14px;
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
}
.precio-dato:last-child { border-right: none; }
.precio-dato--orange { background: #fff8f0; }
.precio-dato--green  { background: #f0faf6; }
.precio-dato-label { font-size: 10px; color: #9e9e9e; font-weight: 600; text-transform: uppercase; margin-bottom: 2px; }
.precio-dato-val   { font-size: 14px; font-weight: 700; color: #212121; }
.precio-dato--orange .precio-dato-val { color: #e65100; }
.precio-dato--green  .precio-dato-val { color: #2e7d32; }
</style>
